import { useEffect, useState, type ReactNode } from 'react'
import { ConsentScreen, type ConsentValue } from './components/ConsentScreen'
import { ShowcaseEntryScreen } from './components/ShowcaseEntryScreen'
import { loadConsent, saveConsent } from './lib/consent-storage'
import { browserLocale } from './lib/i18n'
import { ChatView } from './components/ChatView'
import { useChatStore } from './state/chatStore'
import { fetchShowcase } from './lib/api'
import type { Showcase } from '@k4ia/shared'
import { VisitPage } from './pages/VisitPage'
import { KioskView } from './pages/KioskView'
import { WandPage } from './pages/WandPage'
import { ShowControlPage } from './pages/ShowControlPage'
import { IntroPage } from './pages/IntroPage'
import { IntroRemotePage } from './pages/IntroRemotePage'
import { SchaufensterRemotePage } from './pages/SchaufensterRemotePage'
import { GewinnspielPage } from './pages/GewinnspielPage'
import { BuehnePage } from './pages/BuehnePage'
import { BuehneRemotePage } from './pages/BuehneRemotePage'
import { GewinnspielRemotePage } from './pages/GewinnspielRemotePage'
import { AnsagePage } from './pages/AnsagePage'
import { ThemeToggle } from './components/ThemeToggle'

type Screen = 'loading' | 'consent' | 'entry' | 'chat'

/**
 * FR "NFC-Konzept": `/start[?lab=...]` is the entry tag, `/s/<slug>` a
 * showcase tag. Both just render the same chat shell — the difference is
 * only which greeting/context we kick the conversation off with.
 */
function parseEntryRoute(): { showcaseSlug: string | null } {
  const path = window.location.pathname
  const match = /^\/s\/([^/]+)/.exec(path)
  return { showcaseSlug: match ? decodeURIComponent(match[1]!) : null }
}

/**
 * SPEC "Kiosk-Modus"/"Show-Modus": `/kiosk[?show=1]` is the big screen, `/show/<code>` the staff tablet.
 * `/intro` ist der Intro-Bildschirm, `/intro/go/<token>` die URL auf dem Intro-Tag.
 * `/schaufenster?k=<schlüssel>` ist derselbe Bildschirm im Schaufenster,
 * `/schaufenster/go/<code>` die Adresse in seinem wechselnden QR-Code.
 */
function parseStandaloneRoute(): {
  kiosk: boolean
  /** `/wand`: die Live-Wand im Lab (pages/WandPage.tsx). */
  wand: boolean
  showMode: boolean
  showCode: string | null
  intro: boolean
  introToken: string | null
  schaufenster: string | null
  schaufensterCode: string | null
  /** `/gewinnspiel`: der Bildschirm der Auslosung, `/gewinnspiel/go/<token>` das Handy (pages/Gewinnspiel*). */
  gewinnspiel: boolean
  gewinnspielToken: string | null
  /** `/buehne?k=…`: der gemeinsame Bildschirm (Schlüssel für die Präsentation), `/buehne/go/<token>` die gemeinsame Fernbedienung. */
  buehne: string | null
  buehneToken: string | null
  /** `/ansage/<id>`: K4ia spricht beim Antippen einen festen Text aus dem Admin (pages/AnsagePage.tsx). */
  ansage: string | null
} {
  const path = window.location.pathname
  const showCode = /^\/show\/([^/]+)/.exec(path)?.[1] ?? null
  const introToken = /^\/intro\/go\/([^/]+)/.exec(path)?.[1] ?? null
  const schaufensterCode = /^\/schaufenster\/go\/([^/]+)/.exec(path)?.[1] ?? null
  const gewinnspielToken = /^\/gewinnspiel\/go\/([^/]+)/.exec(path)?.[1] ?? null
  const buehneToken = /^\/buehne\/go\/([^/]+)/.exec(path)?.[1] ?? null
  const ansage = /^\/ansage\/([^/]+)/.exec(path)?.[1] ?? null
  return {
    kiosk: /^\/kiosk\/?$/.test(path),
    wand: /^\/wand\/?$/.test(path),
    showMode: new URLSearchParams(window.location.search).get('show') === '1',
    showCode: showCode ? decodeURIComponent(showCode).toUpperCase() : null,
    intro: /^\/intro\/?$/.test(path),
    introToken: introToken ? decodeURIComponent(introToken) : null,
    // Der Schlüssel des Bildschirms (leer, wenn er fehlt — dann zeigt die Seite das an).
    schaufenster: /^\/schaufenster\/?$/.test(path)
      ? (new URLSearchParams(window.location.search).get('k') ?? '')
      : null,
    schaufensterCode: schaufensterCode ? decodeURIComponent(schaufensterCode) : null,
    gewinnspiel: /^\/gewinnspiel\/?$/.test(path),
    gewinnspielToken: gewinnspielToken ? decodeURIComponent(gewinnspielToken) : null,
    buehne: /^\/buehne\/?$/.test(path)
      ? (new URLSearchParams(window.location.search).get('k') ?? '')
      : null,
    buehneToken: buehneToken ? decodeURIComponent(buehneToken) : null,
    ansage: ansage ? decodeURIComponent(ansage) : null,
  }
}

/** Screens that bypass consent and the chat session set-up entirely. */
function isStandalone(s: ReturnType<typeof parseStandaloneRoute>): boolean {
  return (
    s.kiosk ||
    s.wand ||
    s.showCode !== null ||
    s.intro ||
    s.introToken !== null ||
    s.schaufenster !== null ||
    s.schaufensterCode !== null ||
    s.ansage !== null ||
    s.gewinnspiel ||
    s.gewinnspielToken !== null ||
    s.buehne !== null ||
    s.buehneToken !== null
  )
}

/** SPEC "Besuchs-Zusammenfassung": `/visit/<token>` is opened later from a mailed link. */
function parseVisitToken(): string | null {
  const match = /^\/visit\/([^/]+)/.exec(window.location.pathname)
  return match ? decodeURIComponent(match[1]!) : null
}

export function App() {
  const [screen, setScreen] = useState<Screen>('loading')
  const setConsent = useChatStore((s) => s.setConsent)
  const init = useChatStore((s) => s.init)
  const ready = useChatStore((s) => s.ready)
  // Read once: the summary page is a separate, session-less entry point, so it
  // must render before any consent/session handling kicks in.
  const [visitToken] = useState<string | null>(() => parseVisitToken())
  const [standalone] = useState(() => parseStandaloneRoute())
  /**
   * Einstieg per Showcase-Tag (`/s/<slug>`): Der Besucher steht vor dem Stand.
   * Nach der Einwilligung fragt K4ia nur noch „vorlesen oder lesen?" und
   * erklärt dann sofort — auch wenn schon ein Gespräch läuft. Einmal genutzt,
   * verschwindet der Pfad aus der Adresszeile: Ein Neuladen soll den Showcase
   * nicht noch einmal erklären.
   */
  const [entrySlug] = useState<string | null>(() => parseEntryRoute().showcaseSlug)
  const [entryShowcase, setEntryShowcase] = useState<Showcase | null>(null)
  /** „Vorlesen“ auf dem Tag-Bildschirm anbieten — im Admin abschaltbar. */
  const [entryVorlesen, setEntryVorlesen] = useState(true)
  const [entryChoice, setEntryChoice] = useState<'voice' | 'chat' | null>(null)
  const consent = useChatStore((s) => s.consent)

  useEffect(() => {
    // Die Sprache des Geräts als Startwert — ein englisches Handy sieht die
    // Einwilligung gleich auf Englisch. Was der Besucher danach schreibt,
    // entscheidet weiter (chatStore.noteLanguage).
    useChatStore.getState().setLocale(browserLocale())
  }, [])

  useEffect(() => {
    if (visitToken || isStandalone(standalone)) return
    // Die vollständige Einwilligung zurücklesen, nicht nur ein Ja/Nein: Sonst
    // steht man nach dem Neuladen im Chat, ohne dass Mikrofon und Kamera als
    // freigegeben gelten — und kann nicht mehr sprechen.
    const gespeichert = loadConsent()
    if (gespeichert) setConsent(gespeichert)
    setScreen(gespeichert ? (entrySlug ? 'entry' : 'chat') : 'consent')
  }, [visitToken, standalone, setConsent, entrySlug])

  // Den gescannten Showcase laden, sobald er gebraucht wird. Unbekannter oder
  // nicht geschalteter Slug: gewöhnlicher Chat, ohne Fehlermeldung.
  useEffect(() => {
    if (screen !== 'entry' || !entrySlug || entryShowcase) return
    void fetchShowcase(entrySlug)
      .then(({ showcase, einstieg }) => {
        setEntryVorlesen(einstieg?.vorlesen ?? true)
        setEntryShowcase(showcase)
      })
      .catch(() => {
        window.history.replaceState(null, '', '/')
        setScreen('chat')
      })
  }, [screen, entrySlug, entryShowcase])

  useEffect(() => {
    if (visitToken || isStandalone(standalone) || screen !== 'chat' || ready) return
    void (async () => {
      await init('web')
      if (entryShowcase && entryChoice) {
        await useChatStore.getState().beginShowcaseEntry(entryShowcase, entryChoice)
      }
    })()
  }, [visitToken, standalone, screen, ready, init, entryShowcase, entryChoice])

  function handleConsent(consent: ConsentValue) {
    setConsent(consent)
    saveConsent(consent)
    setScreen(entrySlug ? 'entry' : 'chat')
  }

  function handleEntryChoice(mode: 'voice' | 'chat') {
    // „Vorlesen" ist die ausdrückliche Freigabe des Mikrofons — der Knopf sagt
    // das; die Nachfrage des Browsers folgt ohnehin.
    if (mode === 'voice' && consent && !consent.microphone) {
      const erweitert = { ...consent, microphone: true }
      setConsent(erweitert)
      saveConsent(erweitert)
    }
    window.history.replaceState(null, '', '/')
    setEntryChoice(mode)
    setScreen('chat')
  }

  if (visitToken) return withThemeToggle(<VisitPage token={visitToken} />)
  // Kiosk and show-control are standalone screens: no consent gate (the kiosk
  // states the AI notice itself and never uses the camera) and no NFC context.
  // The kiosk is a shared screen and gets no theme switch.
  if (standalone.showCode) return withThemeToggle(<ShowControlPage code={standalone.showCode} />)
  if (standalone.kiosk) return <KioskView showMode={standalone.showMode} />
  if (standalone.wand) return <WandPage />
  // Intro: der Bildschirm ist wie der Kiosk ein geteilter Screen, das Handy die Fernbedienung.
  if (standalone.intro) return <IntroPage />
  if (standalone.introToken) return <IntroRemotePage token={standalone.introToken} />
  // Schaufenster: dasselbe nach draußen — QR-Code statt Tag, einer steuert.
  if (standalone.schaufenster !== null) {
    return <IntroPage modus="schaufenster" schluessel={standalone.schaufenster} />
  }
  if (standalone.schaufensterCode) {
    return <SchaufensterRemotePage code={standalone.schaufensterCode} />
  }
  if (standalone.buehne !== null) return <BuehnePage schluessel={standalone.buehne} />
  if (standalone.buehneToken) return <BuehneRemotePage token={standalone.buehneToken} />
  if (standalone.ansage) return <AnsagePage id={standalone.ansage} />
  if (standalone.gewinnspiel) return <GewinnspielPage />
  if (standalone.gewinnspielToken) {
    return <GewinnspielRemotePage token={standalone.gewinnspielToken} />
  }
  if (screen === 'loading') return null
  if (screen === 'consent') return withThemeToggle(<ConsentScreen onAccept={handleConsent} />)
  if (screen === 'entry') {
    if (!entryShowcase) return null
    return withThemeToggle(
      <ShowcaseEntryScreen
        showcase={entryShowcase}
        micGranted={consent?.microphone ?? false}
        vorlesen={entryVorlesen}
        onChoose={handleEntryChoice}
      />,
    )
  }
  // The chat carries the switch in its own header, next to the camera button.
  return <ChatView />
}

/** Pins the light/dark switch to the top right corner of a screen. */
function withThemeToggle(screen: ReactNode) {
  return (
    <>
      {screen}
      <ThemeToggle className="fixed right-3 top-3 z-50" />
    </>
  )
}
